import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE_URL } from '../context/api';
import { FaPlus, FaTimes } from 'react-icons/fa';

const Loader = () => (
  <div className="flex items-center justify-center">
    <div className="relative">
      <div className="w-12 h-12 rounded-full absolute border-4 border-pink-200"></div>
      <div className="w-12 h-12 rounded-full animate-spin absolute border-4 border-pink-600 border-t-transparent"></div>
    </div>
  </div>
);

const CreateStudent = () => {
  const [studentData, setStudentData] = useState({
    RegNumber: '',
    Name: '',
    Surname: '',
    DateOfBirth: '',
    NationalIDNumber: '',
    Address: '',
    Gender: '',
    HasMedicalAid: 0,
    PasswordHash: ''
  });

  const [guardians, setGuardians] = useState([{
    Name: '',
    Surname: '',
    NationalIDNumber: '',
    Address: '',
    PhoneNumber: '',
    Gender: ''
  }]);

  const [balanceData, setBalanceData] = useState({
    class_type: '',
    form: '',
    term: '',
    year: new Date().getFullYear(),
    balance: 0.0,
    balance_type: 'DR',
    currency: 'USD'
  });

  const [classTypes, setClassTypes] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch class types from API
  const fetchClassTypes = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/class-types`);
      setClassTypes(response.data);
    } catch (error) {
      console.error('Error fetching class types:', error);
    }
  };

  useEffect(() => {
    fetchClassTypes();
  }, []);

  const handleInputChange = (e, setter) => {
    const { name, value } = e.target;
    setter(prevData => ({ ...prevData, [name]: value }));
  };

  const handleFormChange = (e) => {
    const { value } = e.target;
    setBalanceData(prevData => ({
      ...prevData,
      form: value,
      class_type: '' // Reset class_type when form changes
    }));
  };

  const handleGuardianChange = (index, e) => {
    const { name, value } = e.target;
    const newGuardians = guardians.slice();
    newGuardians[index][name] = value;
    setGuardians(newGuardians);
  };

  const addGuardian = () => {
    setGuardians([...guardians, {
      Name: '',
      Surname: '',   
      NationalIDNumber: '',
      Address: '',
      PhoneNumber: '',
      Gender: ''
    }]);
  };

  const generateRegNumberAndPassword = () => {
    const regNumber = `R${Math.floor(10000 + Math.random() * 90000)}${String.fromCharCode(65 + Math.floor(Math.random() * 26))}`;
    setStudentData(prevData => ({
      ...prevData,
      RegNumber: regNumber,
      PasswordHash: regNumber
    }));
  };

  const clearInputs = () => {
    setStudentData({
      RegNumber: '',
      Name: '',
      Surname: '',
      DateOfBirth: '',
      NationalIDNumber: '',
      Address: '',
      Gender: '',
      HasMedicalAid: 0,
      PasswordHash: ''
    });
    setGuardians([{
      Name: '',
      Surname: '',
      NationalIDNumber: '',
      Address: '',
      PhoneNumber: '',
      Gender: ''
    }]);
    setBalanceData({
      class_type: '',
      form: '',
      term: '',
      year: new Date().getFullYear(),
      balance: 0.0,
      balance_type: 'DR',
      currency: 'USD'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BASE_URL}/createstudent`, {
        studentData,
        guardians,
        balanceData
      });
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error creating student:', error);
      alert('Failed to create student');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    clearInputs();
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Create Student</h1>
            <div className="h-1 w-full bg-gray-200 mt-2"></div>
          </div>

          {loading ? (
            <Loader />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Student Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
                    <input
                      type="text"
                      name="RegNumber"
                      placeholder="Reg Number"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={studentData.RegNumber}
                      readOnly
                      required
                    />
                    <button 
                      type="button"
                      onClick={generateRegNumberAndPassword}
                      className="absolute right-2 top-8 text-pink-600 hover:text-pink-700"
                    >
                      🔄
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      name="Name"
                      placeholder="Name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={studentData.Name}
                      onChange={(e) => handleInputChange(e, setStudentData)}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Surname</label>
                    <input
                      type="text"
                      name="Surname"
                      placeholder="Surname"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={studentData.Surname}
                      onChange={(e) => handleInputChange(e, setStudentData)}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    <input
                      type="date"
                      name="DateOfBirth"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={studentData.DateOfBirth}
                      onChange={(e) => handleInputChange(e, setStudentData)}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">National ID Number</label>
                    <input
                      type="text"
                      name="NationalIDNumber"
                      placeholder="National ID Number"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={studentData.NationalIDNumber}
                      onChange={(e) => handleInputChange(e, setStudentData)}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input
                      type="text"
                      name="Address"
                      placeholder="Address"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={studentData.Address}
                      onChange={(e) => handleInputChange(e, setStudentData)}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <select
                      name="Gender"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={studentData.Gender}
                      onChange={(e) => handleInputChange(e, setStudentData)}
                      required
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Medical Aid</label>
                    <select
                      name="HasMedicalAid"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={studentData.HasMedicalAid}
                      onChange={(e) => handleInputChange(e, setStudentData)}
                    >
                      <option value="0">No Medical Aid</option>
                      <option value="1">Has Medical Aid</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <input
                      type="text"
                      name="PasswordHash"
                      placeholder="Password"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={studentData.PasswordHash}
                      readOnly
                      required
                    />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Guardian Information</h2>
                {guardians.map((guardian, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Guardian Name</label>
                      <input
                        type="text"
                        name="Name"
                        placeholder="Name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={guardian.Name}
                        onChange={(e) => handleGuardianChange(index, e)}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Guardian Surname</label>
                      <input
                        type="text"
                        name="Surname"
                        placeholder="Surname"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={guardian.Surname}
                        onChange={(e) => handleGuardianChange(index, e)}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Guardian National ID Number</label>
                      <input
                        type="text"
                        name="NationalIDNumber"
                        placeholder="National ID Number"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={guardian.NationalIDNumber}
                        onChange={(e) => handleGuardianChange(index, e)}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Guardian Address</label>
                      <input
                        type="text"
                        name="Address"
                        placeholder="Address"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={guardian.Address}
                        onChange={(e) => handleGuardianChange(index, e)}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Guardian Phone Number</label>
                      <input
                        type="text"
                        name="PhoneNumber"
                        placeholder="Phone Number"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={guardian.PhoneNumber}
                        onChange={(e) => handleGuardianChange(index, e)}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Guardian Gender</label>
                      <select
                        name="Gender"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={guardian.Gender}
                        onChange={(e) => handleGuardianChange(index, e)}
                        required
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addGuardian}
                  className="flex items-center text-pink-600 hover:text-pink-700 focus:outline-none"
                >
                  <FaPlus className="mr-2" /> Add Guardian
                </button>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Balance Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Form</label>
                    <select
                      name="form"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={balanceData.form}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select Form</option>
                      {[1, 2, 3, 4, 5, 6].map((form) => (
                        <option key={form} value={form}>Form {form}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Class Type</label>
                    <select
                      name="class_type"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={balanceData.class_type}
                      onChange={(e) => handleInputChange(e, setBalanceData)}
                      required
                    >
                      <option value="">Select Class Type</option>
                      {classTypes.map((type) => (
                        <option key={type.ClassTypeId} value={type.ClassTypeName}>
                          {type.ClassTypeName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
                    <select
                      name="term"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={balanceData.term}
                      onChange={(e) => handleInputChange(e, setBalanceData)}
                      required
                    >
                      <option value="">Select Term</option>
                      {[1, 2, 3].map((term) => (
                        <option key={term} value={term}>Term {term}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                    <input
                      type="number"
                      name="year"
                      placeholder="Year"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={balanceData.year}
                      onChange={(e) => handleInputChange(e, setBalanceData)}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Balance</label>
                    <input
                      type="number"
                      name="balance"
                      placeholder="Enter Balance"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={balanceData.balance}
                      onChange={(e) => handleInputChange(e, setBalanceData)}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Balance Type</label>
                    <select
                      name="balance_type"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={balanceData.balance_type}
                      onChange={(e) => handleInputChange(e, setBalanceData)}
                      required
                    >
                      <option value="DR">Debit</option>
                      <option value="CR">Credit</option>
                    </select>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-pink-600 text-white py-3 px-4 rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Student...' : 'Create Student'}
              </button>
            </form>
          )}

          {showSuccessModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Student Created Successfully!</h3>
                <button
                  onClick={handleCloseModal}
                  className="w-full bg-pink-600 text-white py-2 px-4 rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateStudent;
